import React, { useMemo, useState } from 'react'
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Chip,
  Divider,
  InputBase,
  Tooltip,
  makeStyles
} from '@material-ui/core'
import {
  Close,
  KeyboardArrowUp,
  KeyboardArrowDown,
  Search as SearchIcon,
  PersonOutline,
  ErrorOutline,
  CheckCircleOutline,
  FileCopyOutlined
} from '@material-ui/icons'

import { convertTimestampToLocalString } from 'utils/times'
import { friendlyFcmError } from 'utils/fcmErrors'
import { toast } from 'utils/notify'
import { getTypeColor, getTypeLabel } from './notificationLogsTheme'

const useStyles = makeStyles({
  paper: { width: 480, maxWidth: '92vw' },
  header: {
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    borderBottom: '1px solid #eceff3'
  },
  body: { padding: '20px', overflowY: 'auto' },
  title: { fontSize: 18, fontWeight: 700, lineHeight: 1.3 },
  msgBody: { fontSize: 14, color: '#475569', marginTop: 6, lineHeight: 1.5 },
  metaGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '14px 16px',
    marginTop: 18
  },
  metaLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: 0.6
  },
  metaValue: { fontSize: 13.5, fontWeight: 500, color: '#1e293b', wordBreak: 'break-word' },
  copyRow: { display: 'flex', alignItems: 'center', gap: 4 },
  copyBtn: { padding: 2, color: '#94a3b8', '&:hover': { color: '#475569' } },
  statRow: { display: 'flex', gap: 10, marginTop: 20 },
  statTile: {
    flex: 1,
    borderRadius: 12,
    padding: '12px 14px',
    border: '1px solid #eceff3'
  },
  statNum: { fontSize: 22, fontWeight: 700, lineHeight: 1 },
  statLabel: { fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' },
  segment: {
    display: 'inline-flex',
    background: '#f1f5f9',
    borderRadius: 10,
    padding: 3,
    gap: 2
  },
  segBtn: {
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 12.5,
    fontWeight: 600,
    color: '#64748b',
    padding: '6px 12px',
    borderRadius: 8
  },
  segBtnActive: { background: '#fff', color: '#1e293b', boxShadow: '0 1px 2px rgba(0,0,0,0.08)' },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: '#f1f5f9',
    borderRadius: 10,
    padding: '4px 10px',
    flex: 1
  },
  recipientRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '12px 0',
    borderBottom: '1px solid #f1f5f9'
  },
  rid: { fontSize: 13, fontWeight: 600, color: '#1e293b', wordBreak: 'break-word' },
  remail: { fontSize: 12, color: '#64748b', wordBreak: 'break-all' },
  ridLine: { display: 'flex', alignItems: 'center', gap: 4, marginTop: 1 },
  ridValue: { fontSize: 11, color: '#94a3b8', fontFamily: 'monospace', wordBreak: 'break-all' },
  notice: {
    fontSize: 12,
    color: '#92400e',
    background: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: 8,
    padding: '8px 10px',
    marginBottom: 12
  },
  avatar: {
    width: 30,
    height: 30,
    borderRadius: '50%',
    background: '#e2e8f0',
    color: '#475569',
    fontSize: 12,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  rerr: { fontSize: 12, color: '#b91c1c', marginTop: 2 },
  rerrCode: { fontSize: 11, color: '#94a3b8' },
  footNote: { fontSize: 11.5, color: '#94a3b8', marginTop: 6 }
})

const CONTEXT_FIELDS = [
  ['postId', 'Post ID'],
  ['announcementId', 'Announcement ID'],
  ['questionId', 'Question ID'],
  ['commentId', 'Comment ID'],
  ['replyId', 'Reply ID'],
  ['groupId', 'Group ID'],
  ['itemId', 'Item ID'],
  ['itemType', 'Item Type'],
  ['targetAttendeeId', 'Target Attendee ID']
]

const StatusDot = ({ status }) =>
  status === 'success' ? (
    <CheckCircleOutline style={{ color: '#16a34a', fontSize: 18, marginTop: 1 }} />
  ) : (
    <ErrorOutline style={{ color: '#dc2626', fontSize: 18, marginTop: 1 }} />
  )

const fullName = (attendee) =>
  attendee ? [attendee.firstName, attendee.lastName].filter(Boolean).join(' ').trim() : ''

const initials = (name) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('') || '?'

const copyToClipboard = (value) => (e) => {
  e.stopPropagation()
  navigator.clipboard?.writeText(value).then(
    () => toast.success('Copied to clipboard'),
    () => toast.error('Failed to copy')
  )
}

const CopyButton = ({ value, classes, size = 15 }) => (
  <Tooltip title="Copy">
    <IconButton size="small" className={classes.copyBtn} onClick={copyToClipboard(value)}>
      <FileCopyOutlined style={{ fontSize: size }} />
    </IconButton>
  </Tooltip>
)

const CopyableId = ({ value, classes }) => (
  <div className={classes.copyRow}>
    <span className={classes.metaValue}>{value}</span>
    <CopyButton value={value} classes={classes} />
  </div>
)

const NotificationLogDrawer = ({
  open,
  log,
  conference,
  attendeesById,
  onClose,
  onPrev,
  onNext,
  hasPrev,
  hasNext
}) => {
  const classes = useStyles()
  const [statusFilter, setStatusFilter] = useState('all')
  const [search, setSearch] = useState('')

  const lookup = useMemo(() => attendeesById || new Map(), [attendeesById])

  const initiatedBy = useMemo(() => {
    if (!log) return '—'
    if (log.sourceAdminId) return 'Admin'
    if (log.sourceAttendeeId) {
      const name = fullName(lookup.get(log.sourceAttendeeId))
      return name ? `Attendee · ${name}` : `Attendee · ${log.sourceAttendeeId}`
    }
    return '—'
  }, [log, lookup])

  // Recipients now live in two inline arrays on the doc. Delivered come from
  // successfulRecipients (attendeeId strings); failed from failedRecipients
  // ({ attendeeId, error }). Counts are read from successCount/failureCount,
  // never array lengths (successfulRecipients may be pruned after archiving).
  const recipients = useMemo(() => {
    if (!log) return []
    const delivered = (log.successfulRecipients || []).map((attendeeId) => ({
      attendeeId,
      status: 'success'
    }))
    const failed = (log.failedRecipients || []).map((f) => ({
      attendeeId: f.attendeeId,
      status: 'failed',
      error: f.error
    }))
    return [...failed, ...delivered]
  }, [log])

  // True when the delivered list was dropped after archiving but the send did
  // have successes (count > 0) — we then show the count without a per-name list.
  const successPruned = !!log?.successRecipientsPruned && (log?.successCount ?? 0) > 0
  const storedFailures = (log?.failedRecipients || []).length

  const filtered = useMemo(() => {
    let list = recipients
    if (statusFilter !== 'all') list = list.filter((r) => r.status === statusFilter)
    const q = search.trim().toLowerCase()
    if (q) {
      list = list.filter((r) => {
        const attendee = r.attendeeId ? lookup.get(r.attendeeId) : null
        return (
          r.attendeeId?.toLowerCase().includes(q) ||
          fullName(attendee).toLowerCase().includes(q) ||
          attendee?.email?.toLowerCase().includes(q) ||
          r.error?.code?.toLowerCase().includes(q)
        )
      })
    }
    return list
  }, [recipients, statusFilter, search, lookup])

  const typeColor = getTypeColor(log?.notificationType)

  const segments = [
    { key: 'all', label: 'All' },
    { key: 'success', label: 'Delivered' },
    { key: 'failed', label: 'Failed' }
  ]

  return (
    <Drawer anchor="right" open={open} onClose={onClose} classes={{ paper: classes.paper }}>
      {!log ? null : (
        <>
          <div className={classes.header}>
            <Chip
              size="small"
              label={getTypeLabel(log.notificationType)}
              style={{ backgroundColor: typeColor.bg, color: typeColor.fg, fontWeight: 600 }}
            />
            <span style={{ flex: 1 }} />
            <Tooltip title="Previous (older)">
              <span>
                <IconButton size="small" onClick={onNext} disabled={!hasNext}>
                  <KeyboardArrowDown />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Next (newer)">
              <span>
                <IconButton size="small" onClick={onPrev} disabled={!hasPrev}>
                  <KeyboardArrowUp />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton size="small" onClick={onClose}>
                <Close />
              </IconButton>
            </Tooltip>
          </div>

          <div className={classes.body}>
            <Typography className={classes.title}>
              {log?.content?.notification?.title || '—'}
            </Typography>
            <Typography className={classes.msgBody}>
              {log?.content?.notification?.body || 'No message body'}
            </Typography>

            <div className={classes.metaGrid}>
              <div>
                <div className={classes.metaLabel}>Sent At</div>
                <div className={classes.metaValue}>
                  {log.createdAt
                    ? convertTimestampToLocalString(
                        log.createdAt,
                        conference?.timezone,
                        conference?.is24hrFormat
                      )
                    : '—'}
                </div>
              </div>
              <div>
                <div className={classes.metaLabel}>Initiated By</div>
                <div className={classes.metaValue}>
                  <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                    <PersonOutline style={{ fontSize: 15, color: '#94a3b8' }} />
                    {initiatedBy}
                  </Box>
                </div>
              </div>
              {CONTEXT_FIELDS.filter(([key]) => log[key] !== undefined && log[key] !== null).map(
                ([key, label]) => (
                  <div key={key}>
                    <div className={classes.metaLabel}>{label}</div>
                    <CopyableId value={String(log[key])} classes={classes} />
                  </div>
                )
              )}
            </div>

            <div className={classes.statRow}>
              <div className={classes.statTile}>
                <div className={classes.statNum} style={{ color: '#1e293b' }}>
                  {log.totalCount ?? 0}
                </div>
                <div className={classes.statLabel}>Total</div>
              </div>
              <div
                className={classes.statTile}
                style={{ borderColor: '#bbf7d0', background: '#f0fdf4' }}>
                <div className={classes.statNum} style={{ color: '#15803d' }}>
                  {log.successCount ?? 0}
                </div>
                <div className={classes.statLabel}>Delivered</div>
              </div>
              <div
                className={classes.statTile}
                style={{ borderColor: '#fecaca', background: '#fef2f2' }}>
                <div className={classes.statNum} style={{ color: '#b91c1c' }}>
                  {log.failureCount ?? 0}
                </div>
                <div className={classes.statLabel}>Failed</div>
              </div>
            </div>
            <div className={classes.footNote}>
              “Delivered” = accepted by FCM for delivery to the device. Reads are not tracked.
            </div>

            <Divider style={{ margin: '20px 0 16px' }} />

            <Box display="flex" alignItems="center" style={{ gap: 10, marginBottom: 14 }}>
              <div className={classes.segment}>
                {segments.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    className={`${classes.segBtn} ${
                      statusFilter === s.key ? classes.segBtnActive : ''
                    }`}
                    onClick={() => setStatusFilter(s.key)}>
                    {s.label}
                  </button>
                ))}
              </div>
              <div className={classes.searchBox}>
                <SearchIcon style={{ fontSize: 18, color: '#94a3b8' }} />
                <InputBase
                  placeholder="Search recipient"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ fontSize: 13 }}
                />
              </div>
            </Box>

            {successPruned && statusFilter !== 'failed' ? (
              <div className={classes.notice}>
                {log.successCount} recipient{log.successCount === 1 ? '' : 's'} delivered. The
                delivered list was pruned after archiving, so individual names aren’t available.
              </div>
            ) : null}
            {log?.failedTruncated && statusFilter !== 'success' ? (
              <div className={classes.notice}>
                Showing first {storedFailures} of {log.failureCount} failures.
              </div>
            ) : null}

            {filtered.length ? (
              <div>
                {filtered.map((r, idx) => {
                  const attendee = r.attendeeId ? lookup.get(r.attendeeId) : null
                  const name = fullName(attendee)
                  const primary = name || r.attendeeId || 'Unmapped recipient'
                  return (
                    <div className={classes.recipientRow} key={`${r.attendeeId || 'na'}-${idx}`}>
                      <div className={classes.avatar}>{initials(name || r.attendeeId || '')}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span className={classes.rid}>{primary}</span>
                        {attendee?.email ? (
                          <div className={classes.remail}>{attendee.email}</div>
                        ) : null}
                        {r.attendeeId ? (
                          <div className={classes.ridLine}>
                            {/* Show the raw id line only when the name is the primary
                                label; for unmapped recipients the id is already primary. */}
                            {name ? <span className={classes.ridValue}>{r.attendeeId}</span> : null}
                            <CopyButton value={r.attendeeId} classes={classes} size={13} />
                          </div>
                        ) : null}
                        {r.status === 'failed' && r.error ? (
                          <>
                            <div className={classes.rerr}>{friendlyFcmError(r.error.code)}</div>
                            <div className={classes.rerrCode}>{r.error.code}</div>
                            {r.error.message ? (
                              <div className={classes.rerrCode}>{r.error.message}</div>
                            ) : null}
                          </>
                        ) : null}
                      </div>
                      <Tooltip title={r.status === 'success' ? 'Delivered' : 'Failed'}>
                        <span style={{ display: 'flex' }}>
                          <StatusDot status={r.status} />
                        </span>
                      </Tooltip>
                    </div>
                  )
                })}
              </div>
            ) : !successPruned ? (
              <Typography style={{ fontSize: 13, color: '#94a3b8', padding: '12px 0' }}>
                No recipients match this filter.
              </Typography>
            ) : null}
          </div>
        </>
      )}
    </Drawer>
  )
}

export default NotificationLogDrawer
